import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import classNames from "classnames";
import React, { Fragment, ReactNode, useEffect, useRef, useState } from "react";

export interface ModalProps {
  title?: ReactNode;
  open: boolean;
  setOpen: (val: boolean) => void;
  onOpen?: () => void;
  onClose?: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  hideHeader?: boolean | undefined;
}

export function Modal(props: ModalProps) {
  /* this is a bit of a hack to fix body scrolling in safari, want to figure out a better solution */
  const scrollY = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (props.open) {
      scrollY.current = window.scrollY;
      document.body.classList.add("fixed", "inset-0", "overflow-hidden");
      return () => {
        document.body.classList.remove("fixed", "inset-0", "overflow-hidden");
        if (scrollY.current) {
          window.scrollTo(0, scrollY.current);
        }
        scrollY.current = undefined;
      };
    }
  }, [props.open]);

  return (
    <Transition.Root appear show={props.open} as={Fragment}>
      <Dialog
        as="div"
        className="sm:overflow-y-auto fixed inset-0 z-50 overflow-hidden"
        onClose={props.setOpen}
      >
        <div
          className={classNames(
            "fixed inset-y-0 justify-center text-center",
            "sm:relative sm:min-h-screen sm:px-4 sm:top-0",
          )}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="bg-black/40 fixed inset-0 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="sm:inline-block sm:align-middle sm:h-screen hidden" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-400"
            enterFrom="translate-y-full sm:opacity-0 sm:translate-y-0"
            enterTo="sm:opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in-out duration-400"
            leaveFrom="sm:opacity-100 translate-y-0 sm:scale-100"
            leaveTo="sm:opacity-0 translate-y-full sm:translate-y-0"
          >
            <div
              className={classNames(
                "fixed top-auto w-full bottom-0 inset-x-0 inline-block align-bottom bg-background rounded-t-2xl text-left shadow-xl transform transition-all",
                "sm:top-0 sm:inset-0 sm:relative sm:my-8 sm:align-middle sm:rounded",
                {
                  "max-w-lg": props.size === "xs",
                  "max-w-screen-sm": props.size === "sm",
                  "max-w-screen-md": props.size === "md" || !props.size, // default
                  "max-w-screen-lg": props.size === "lg",
                },
              )}
            >
              {!props.hideHeader && (
                <div className="flex items-center justify-between p-5 rounded-t-sm">
                  {props.title ? (
                    <Dialog.Title as="h3" className="text-foreground text-lg font-semibold">
                      {props.title}
                    </Dialog.Title>
                  ) : (
                    <div />
                  )}
                  <button
                    type="button"
                    onClick={() => props.setOpen(false)}
                    className="text-muted-foreground bg-transparent hover:bg-muted hover:text-foreground rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                    data-modal-toggle="small-modal"
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              )}
              <div className="sm:overflow-hidden sm:py-0 h-full pb-32 overflow-y-auto">
                {props.children}
              </div>
              {props.footer && <ModalFooter>{props.footer}</ModalFooter>}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

type ModalContentProps = {
  children: ReactNode;
};

function ModalContent({ children }: ModalContentProps) {
  return <div className="sm:p-6 p-4">{children}</div>;
}

Modal.Content = ModalContent;

type ModalFooterProps = {
  children: ReactNode;
};

function getResolvedChildren(children: ReactNode): ReactNode {
  if (!React.isValidElement(children)) return children;
  return children.type === React.Fragment
    ? (children.props as { children?: ReactNode }).children
    : children;
}

function ModalFooter({ children }: ModalFooterProps) {
  return (
    <div
      className={classNames(
        "fixed inset-x-0 bottom-0 w-full p-4 rounded-b",
        "sm:relative sm:shadow-none sm:p-6",
      )}
    >
      {React.Children.map(getResolvedChildren(children), (child) => {
        if (!React.isValidElement(child)) return child;
        const childProps = child.props as { className?: string };
        return React.cloneElement(child, {
          className: `${childProps.className || ""} w-full sm:w-auto`,
        } as React.Attributes);
      })}
    </div>
  );
}

Modal.Footer = ModalFooter;

export function useModal(props: Omit<ModalProps, "open" | "setOpen">) {
  const [open, setOpen] = useState(false);
  function _setOpen(open: boolean) {
    if (open) {
      props.onOpen?.();
    } else {
      props.onClose?.();
    }
    setOpen(open);
  }

  return {
    open,
    setOpen,
    Modal: <Modal {...props} open={open} setOpen={_setOpen} />,
  };
}
